import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/dialog';
import { Icon } from '@/components/icon';
import { cn } from '@/lib';
import { Command as CommandPrimitive } from 'cmdk';
import React from 'react';

const Command: React.FC<React.ComponentProps<typeof CommandPrimitive>> = ({ className, ...props }) => {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        'flex',
        'h-full',
        'w-full',
        'flex-col',
        'bg-[var(--base-popover)]',
        'text-[var(--base-popover-foreground)]',
        'rounded-[var(--border-radius-lg)]',
        'border',
        'border-[var(--base-border)]',
        className,
      )}
      {...props}
    />
  );
};

const CommandDialog: React.FC<
  React.ComponentProps<typeof Dialog> & {
    title?: string;
    description?: string;
    className?: string;
  }
> = ({ title = 'Command Palette', description = 'Search for a command to run...', children, className, ...props }) => {
  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent className={cn('overflow-hidden p-0', className)} hasCloseButton={false}>
        {children}
      </DialogContent>
    </Dialog>
  );
};

const CommandInput: React.FC<React.ComponentProps<typeof CommandPrimitive.Input> & { suffix?: React.ReactNode }> = ({
  className,
  suffix,
  ...props
}) => {
  return (
    <div
      data-slot="command-input-wrapper"
      className={cn(
        'flex',
        'h-[var(--height-h-10)]',
        'items-center',
        'gap-[var(--spacing-3)]',
        'border-[var(--base-border)]',
        'py-[var(--spacing-2)]',
        // If `end` jsx exist, it will handle its own padding right
        suffix ? 'pl-[var(--spacing-3)]' : 'px-[var(--spacing-3)]',
        'bg-[var(--base-background)]',
        className,
      )}
    >
      <Icon name="Search" size={16} className="shrink-0 text-[var(--base-muted-foreground)]" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          '!placeholder:text-[var(--base-muted-foreground)]',
          'flex',
          'w-full',
          'text-[length:var(--typography-typography-components-small-font-size)]',
          'outline-hidden',
          'disabled:cursor-not-allowed',
          'disabled:opacity-[var(--opacity-50)]',
        )}
        {...props}
      />
      {suffix}
    </div>
  );
};

const CommandList: React.FC<React.ComponentProps<typeof CommandPrimitive.List>> = ({ className, ...props }) => {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn('max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto', className)}
      {...props}
    />
  );
};

const CommandEmpty: React.FC<React.ComponentProps<typeof CommandPrimitive.Empty>> = ({ ...props }) => {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-[var(--spacing-6)] text-center text-[length:var(--typography-typography-components-small-font-size)]"
      {...props}
    />
  );
};

const CommandGroup: React.FC<React.ComponentProps<typeof CommandPrimitive.Group>> = ({ className, ...props }) => {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
        className,
      )}
      {...props}
    />
  );
};

const CommandSeparator: React.FC<React.ComponentProps<typeof CommandPrimitive.Separator>> = ({ className, ...props }) => {
  return <CommandPrimitive.Separator data-slot="command-separator" className={cn('bg-[var(--base-muted)] h-px', className)} {...props} />;
};

const CommandItem: React.FC<React.ComponentProps<typeof CommandPrimitive.Item> & { htmlFor?: string }> = ({ className, ...props }) => {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        'data-[selected=true]:bg-[var(--base-accent)]',
        'data-[selected=true]:text-[var(--base-accent-foreground)]',
        "[&_svg:not([class*='text-'])]:text-[var(--base-muted-foreground)]",
        'relative',
        'flex',
        'cursor-default',
        'items-center',
        'gap-[var(--spacing-2)]',
        'rounded-sm',
        'px-[var(--spacing-2)]',
        'py-[var(--spacing-1-5)]',
        'text-[length:var(--typography-typography-components-small-font-size)]',
        'outline-hidden',
        'select-none',
        'data-[disabled=true]:pointer-events-none',
        'data-[disabled=true]:opacity-[var(--opacity-50)]',
        '[&_svg]:pointer-events-none',
        '[&_svg]:shrink-0',
        '[&_svg:not([class*="size-"])]:size-4',
        'hover:bg-[var(--base-accent)]',
        'hover:cursor-pointer',
        className,
      )}
      {...props}
    />
  );
};

const CommandShortcut: React.FC<React.ComponentProps<'span'>> = ({ className, ...props }) => {
  return <span data-slot="command-shortcut" className={cn('text-muted-foreground ml-auto text-xs tracking-widest', className)} {...props} />;
};

const CommandLoading: React.FC<React.ComponentProps<typeof CommandPrimitive.Loading>> = ({ ...props }) => {
  return <CommandPrimitive.Loading {...props} />;
};

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
  CommandLoading,
};
